import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stats, api } from "../lib/api";

const pct = (x: number) => `${Math.round(x * 100)}%`;

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card">
      <div className="text-xs font-mono text-scribe-600 uppercase">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
      {hint && <div className="text-xs text-scribe-600 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .stats()
      .then(setStats)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-ink-700">Chargement…</div>;

  if (!stats || stats.total_attempts === 0) {
    return (
      <div>
        <h1 className="font-display text-3xl mb-6">Tableau de bord</h1>
        <div className="card max-w-md">
          <p className="text-sm text-ink-700 mb-4">
            Aucune donnée pour l'instant. Réponds à quelques questions et tes statistiques
            apparaîtront ici : précision par domaine, série de jours, activité.
          </p>
          <Link to="/certs" className="btn-primary inline-flex">
            Commencer une session
          </Link>
        </div>
      </div>
    );
  }

  const maxDay = Math.max(1, ...stats.last_days.map((d) => d.attempts));

  return (
    <div>
      <h1 className="font-display text-3xl mb-6">Tableau de bord</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Tentatives" value={String(stats.total_attempts)} />
        <StatCard
          label="Précision"
          value={pct(stats.accuracy)}
          hint={`${stats.total_correct} correctes`}
        />
        <StatCard
          label="Série"
          value={`${stats.streak_days} j`}
          hint={stats.streak_days > 0 ? "jours consécutifs" : "reprends aujourd'hui"}
        />
        <StatCard
          label="À réviser"
          value={String(stats.due_today)}
          hint={stats.due_today > 0 ? "cartes dues aujourd'hui" : "tout est à jour"}
        />
      </div>

      {/* Activité 14 jours */}
      <div className="card mt-6">
        <div className="text-xs font-mono text-scribe-600 uppercase mb-3">
          Activité — 14 derniers jours
        </div>
        <div className="flex items-end gap-1 h-20" aria-label="activité quotidienne">
          {stats.last_days.map((d) => (
            <div
              key={d.date}
              title={`${d.date} : ${d.attempts} tentative(s), ${d.correct} correcte(s)`}
              className="flex-1 rounded-t bg-lapis-500/70"
              style={{ height: `${(d.attempts / maxDay) * 100}%`, minHeight: d.attempts > 0 ? 4 : 1 }}
            />
          ))}
        </div>
      </div>

      {/* Par certification / domaine, faiblesses en premier */}
      {stats.per_cert.map((cert) => (
        <div key={cert.cert_id} className="card mt-6">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl">{cert.cert_id}</h2>
            <span className="text-sm font-mono text-scribe-600">
              {pct(cert.accuracy)} · {cert.attempts} tentatives
            </span>
          </div>
          <ul className="space-y-3">
            {cert.domains.map((d) => (
              <li key={d.domain}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="min-w-0 truncate">{d.domain}</span>
                  <span className="font-mono text-scribe-600 shrink-0 ml-4">
                    {pct(d.accuracy)} ({d.correct}/{d.attempts})
                  </span>
                </div>
                <div className="h-2 rounded bg-papyrus-200 overflow-hidden">
                  <div
                    className={`h-full rounded ${d.accuracy < 0.6 ? "bg-red-600/70" : d.accuracy < 0.8 ? "bg-scribe-500" : "bg-lapis-500"}`}
                    style={{ width: pct(d.accuracy) }}
                  />
                </div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-scribe-600 mt-3">Domaines triés du plus faible au plus fort.</p>
        </div>
      ))}
    </div>
  );
}
